import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_FONT_WEIGHT
} from "constants/chart.constants";
import {
  EvaChart,
  RigFracActivityMetric,
  RigFracGroupBy,
  RigFracTimeScale
} from "constants/charts.enums";
import { EChartsOption, TooltipComponentPositionCallback } from "echarts";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { resolveRigFracMetrics } from "entities/charts/eva/requests/rigFracMetrics";
import { createEntityState } from "entities/charts/factory";
import { ChartOption, DEFAULT_CHART_OPTIONS } from "entities/charts/options";
import { AxisLockOption, AxisLockPosition } from "types/charts/locks";
import { EvaChartStates } from "types/factory";
// Straight from the key helper rather than the focusColorSync barrel: the
// barrel also re-exports the resolution builder and the gate, which would drag
// appSlice (and everything it imports) into this pure translator's module graph.
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import { removeCount } from "utils/removeCountFromTitle";

import { DataEnum } from "models";

import { getRigFracGroupByLabel } from "components/charts/rig-frac/groupByOptions";

import { createChartLocks } from "../locks";
import {
  AXIS_LABEL_COLOR,
  AXIS_TITLE_COLOR,
  DEFAULT_Z_INDEX,
  WELL_COUNT_SERIES_COLOR,
  WELL_COUNT_SERIES_WIDTH
} from "./constants";
import {
  buildRigFracItemTooltipFormatter,
  rigFracBucketLabel,
  rigFracMetricLabel
} from "./rigFracTooltip";

// A y-axis option carrying the lock config the AxisContainer/AxisLocker read
// from the persisted options JSON (same shape TotalRateDate emits).
type YAXisOptionWithLock = Partial<YAXisOption> & {
  lock?: AxisLockOption;
  lockPosition?: AxisLockPosition;
  isTotalRateDateLockPosition?: boolean;
};

// Whole-number axis labels (spec FR-007): counts only, never decimals. Rounds
// defensively — with minInterval 1 ECharts should already emit integers.
const integerAxisLabelFormatter = (value: number) => Math.round(value).toLocaleString();

// Integer value-axis shared config. minInterval 1 prevents fractional ticks on
// small ranges; max guard below handles the min==max single-value case, which
// otherwise collapses the axis (known ECharts value-axis gotcha). Exported so
// the Recent Activity translator applies the identical integer scale to its
// value axis (the x-axis, since its bars are horizontal).
//
// EXPORTED AND SHARED across four axes (rig/frac yAxis1, yAxis2,
// wellStatusCurrent.ts:135, rigFracRecentActivity.ts) — never mutate this
// object in place (H3). A price series needs a DIFFERENT, separate config
// (priceValueAxisOptions below); mutating this one decimal-aware would
// silently break the two unrelated integer-only charts that spread it.
export const integerValueAxisOptions = {
  minInterval: 1,
  axisLabel: {
    fontWeight: DEFAULT_FONT_WEIGHT,
    color: AXIS_LABEL_COLOR,
    fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
    formatter: integerAxisLabelFormatter
  }
};

// EVA-149. Price axis labels render 2 decimal places (a WTI/Henry Hub price
// like "61.26"), never rounded to whole dollars, and — unlike the integer
// axis — no `minInterval: 1`, which would round a sub-$1 tick spacing back up
// to whole numbers. A SEPARATE object from integerValueAxisOptions (H3): only
// ever spread into yAxis2, and only when the right metric is a price.
const priceAxisLabelFormatter = (value: number) => value.toFixed(2);

// Not exported — no consumer outside this file needs it (H3's own warning:
// exporting integerValueAxisOptions above is what made mutating it dangerous;
// keep this one's blast radius to zero).
const priceValueAxisOptions = {
  axisLabel: {
    fontWeight: DEFAULT_FONT_WEIGHT,
    color: AXIS_LABEL_COLOR,
    fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
    formatter: priceAxisLabelFormatter
  }
};

// The right metric decides which of the two axis option sets applies to
// yAxis2 — every OTHER caller of integerValueAxisOptions (yAxis1 here,
// wellStatusCurrent.ts, rigFracRecentActivity.ts) is unconditionally integer
// and untouched by this feature.
// Exported so RigFracMetricSelector can gate its y2-lock reset on an actual
// price<->count TRANSITION, not on every secondaryMetric write. Unlike
// integerValueAxisOptions above, exporting this carries none of the H3 risk:
// it's a pure predicate (metric in, boolean out) with no shared state to
// alias or mutate, not a shared config object a caller could corrupt in place.
export const isPriceMetric = (metric: Maybe<RigFracActivityMetric>): boolean =>
  metric === RigFracActivityMetric.WtiPrice ||
  metric === RigFracActivityMetric.HenryHubPrice;

// Accepts nullable series (the line path can carry genuine null gap weeks —
// EC-001, T041) as well as the bar path's always-numeric arrays. Filters nulls
// out of its own reduce BEFORE spreading into Math.max, so a null-only series
// can't collapse the max to NaN/0 (H-EC-001 — see toLineValues below).
// Returns undefined (not 0) when there is no data, like seriesMin below, so
// callers can tell "no values" from "the maximum is legitimately 0". A 0 seed
// would be a count assumption: a price can be negative (WTI settled at -$37.63
// on 2020-04-20 and the collection runs back to ~2000).
const seriesMax = (ys: Array<Array<Maybe<number>>>): Maybe<number> => {
  const values = ys.flatMap((y) => y.filter((v): v is number => v != null));
  return values.length > 0 ? Math.max(...values) : undefined;
};

// Mirrors seriesMax but for the minimum — used ONLY to seed the y2 MIN lock
// input's display default for a price right-metric (EVA-149; see y2MinLockValue
// below). Returns undefined (not 0) when there is no data at all, so callers
// can distinguish "no data" from "the data's minimum is legitimately 0".
const seriesMin = (ys: Array<Array<Maybe<number>>>): Maybe<number> => {
  const values = ys.flatMap((y) => y.filter((v): v is number => v != null));
  return values.length > 0 ? Math.min(...values) : undefined;
};

// When every value is 0 (empty result) or a single flat value, give the axis
// explicit integer bounds so ECharts cannot produce a degenerate/fractional scale.
export const guardedAxisMax = (max: number): number => (max <= 0 ? 1 : Math.ceil(max));

// Grid insets tuned for this widget's chrome (x + per-axis y dataZoom sliders —
// see RIG_FRAC_ACTIVITY_DATA_ZOOM below; the y slider bands overlap these insets
// by design, only their end handles are visible). The legend floats OVER the
// chart (shared ChartLegend), so no plot space is reserved for it; left/right
// only need to clear the rotated vertical y-axis titles + integer labels.
// containLabel is off, so these fixed insets must be wide enough that the axis
// names aren't clipped (bug: missing titles).
// Bottom only reserves room for the x labels + x-axis title — NOT for the
// bottom-center TimeScaleSelector, which floats over the axis on hover — so the
// plot isn't left with a tall empty band beneath it.
const GRID_LEFT_RIGHT = 66;
// Grid top — plot-area top edge (container-relative). Lock-handle MAX position
// keys off this. Also the anchor the screenshot path reserves against.
const GRID_TOP = 46;
// Bottom reserves room for the rotated (45°) date labels + the x-axis title AND
// the bottom x-axis dataZoom slider (#1) beneath them.
// Exported: TimeScaleSelector derives the "Time" title's distance from the
// widget bottom (GRID_BOTTOM_ROTATED − xAxis.nameGap) to overlay it on hover.
export const GRID_BOTTOM_ROTATED = 100;
const GRID_BOTTOM_FLAT = 48; // horizontal labels + title

const buildRigFracGrid = (labelsRotated: boolean) => ({
  top: GRID_TOP,
  left: GRID_LEFT_RIGHT,
  right: GRID_LEFT_RIGHT,
  bottom: labelsRotated ? GRID_BOTTOM_ROTATED : GRID_BOTTOM_FLAT,
  containLabel: false
});

// Vertical extent (px) a 45°-rotated axis label occupies, estimated from the
// longest bucket label and its font size. 0.707 = sin(45°); ~0.5·font ≈ mean
// proportional char width at this size.
const rotatedLabelExtent = (labels: string[], fontSize: number): number => {
  const longest = labels.reduce((max, l) => Math.max(max, (l ?? "").length), 0);
  const textWidth = longest * fontSize * 0.5;
  return Math.ceil(0.707 * (textWidth + fontSize * 1.2));
};

// x-axis "Time" title gap: label extent + a margin of clear space beneath the
// slanted labels. Size-aware so the gap holds for short quarterly labels
// ("2025-Q3") and long custom/weekly ones ("Jul 13, 2026") alike, and scales
// with the screenshot font bump (the screenshot transform re-scales it by the
// font-bump ratio). The 16px margin is the requested visible breathing room
// between the labels and the title (a smaller margin read as touching).
export const rigFracXNameGap = (labels: string[], fontSize: number): number =>
  rotatedLabelExtent(labels, fontSize) + 16;

// ——— dataZoom (#6) ———
// Built from the wells DEFAULT_CHART_OPTIONS entries (transparent track + handle
// styling), reshaped for this widget: the x slider TOUCHES the x-axis line (its
// top sits at the axis: bottom = grid.bottom − height; grid.bottom leaves the
// band below for the slanted labels + "Time" title), and EVERY zoom is pinned to
// exactly one axis, with the right y-axis getting its OWN slider. The default
// `inside` vertical zoom carries no yAxisIndex, and ECharts auto-targets an
// unpinned dataZoom at every parallel axis in the grid; dataZooms sharing an
// axis are then range-linked, so the left slider (axis 0) zoomed BOTH axes
// through it. Pinned per-axis, the slider↔inside pairs form two independent
// link groups: each slider moves only its axis, while wheel/pinch still zooms
// both (each inside zoom answers the gesture for its own axis).
// NOTE: the builder keys off DEFAULT_CHART_OPTIONS.dataZoom entry shapes (the
// slider ids, the inside zooms' orient) — that structure is load-bearing. If the
// shared default is restructured, revisit these constants; the tests count the
// emitted entries exactly and assert each is pinned, so a silent fall-through
// or a new unpinned entry fails there.
const X_AXIS_SLIDER_ID = "xAxis-slider";
const Y_AXIS_SLIDER_ID = "yAxis-slider";
const Y2_AXIS_SLIDER_ID = "yAxis2-slider";
const X_AXIS_INSIDE_ID = "xAxis-inside";
const Y_AXIS_INSIDE_ID = "yAxis-inside";
const Y2_AXIS_INSIDE_ID = "yAxis2-inside";

const X_SLIDER_HEIGHT = 21;
// Both y sliders sit this far inside their plot edge — the wells default's
// left: 50, mirrored to right: 50 for the y2 slider. [EYEBALL]
const Y_SLIDER_EDGE_INSET = 50;
// ECharts' default vertical-slider track width, stated so the layout band the
// sliders occupy is explicit (only the end handles are visible).
const Y_SLIDER_TRACK_WIDTH = 30;

// The floating ChartLegend defaults to the NE corner (x = width − legend − 20),
// directly ON TOP of the y2 slider's MAX handle — and the legend is a DOM
// overlay, so the canvas-drawn handle beneath it can never receive the pointer.
// This chart passes the legend a default x-buffer that clears the slider band
// (edge inset + track width + a small gap); the user can still drag it anywhere.
export const RIG_FRAC_ACTIVITY_LEGEND_X_BUFFER =
  Y_SLIDER_EDGE_INSET + Y_SLIDER_TRACK_WIDTH + 10;

// The subset of an ECharts dataZoom option this builder reads/writes (same
// pattern as the box-plot builder's entry interface).
interface DataZoomEntry {
  id?: string;
  type?: string;
  orient?: string;
  filterMode?: string;
  xAxisIndex?: number;
  yAxisIndex?: number;
  left?: Nullable<number>;
  right?: number;
  width?: number;
  bottom?: number;
  height?: string;
  [key: string]: unknown;
}

// Every branch returns spread COPIES with geometry + filterMode set explicitly,
// never the shared default objects by reference: the wells spacing pass mutates
// its input in place (slider.left) and is handed the shared default array on
// some paths, so an aliased entry could be shifted by another chart's render
// mid-session. filterMode "none" is pinned rather than inherited — in "filter"
// mode a y zoom would DROP out-of-range bars/line points instead of clipping.
const RIG_FRAC_ACTIVITY_DATA_ZOOM: DataZoomEntry[] = (
  (DEFAULT_CHART_OPTIONS.dataZoom ?? []) as DataZoomEntry[]
).flatMap((dz): DataZoomEntry[] => {
  if (dz.id === X_AXIS_SLIDER_ID) {
    return [
      {
        ...dz,
        bottom: GRID_BOTTOM_ROTATED - X_SLIDER_HEIGHT,
        height: `${X_SLIDER_HEIGHT}px`,
        filterMode: "none"
      }
    ];
  }
  if (dz.id === Y_AXIS_SLIDER_ID) {
    return [
      {
        ...dz,
        yAxisIndex: 0,
        left: Y_SLIDER_EDGE_INSET,
        width: Y_SLIDER_TRACK_WIDTH,
        filterMode: "none"
      },
      {
        ...dz,
        id: Y2_AXIS_SLIDER_ID,
        yAxisIndex: 1,
        // null — the slider's documented default — unsets the inherited left so
        // the right inset positions the track.
        left: null,
        right: Y_SLIDER_EDGE_INSET,
        width: Y_SLIDER_TRACK_WIDTH,
        filterMode: "none"
      }
    ];
  }
  if (dz.type === "inside") {
    return dz.orient === "vertical"
      ? [
          { ...dz, id: Y_AXIS_INSIDE_ID, yAxisIndex: 0, filterMode: "none" },
          { ...dz, id: Y2_AXIS_INSIDE_ID, yAxisIndex: 1, filterMode: "none" }
        ]
      : [{ ...dz, id: X_AXIS_INSIDE_ID, xAxisIndex: 0, filterMode: "none" }];
  }
  return [{ ...dz }];
});

// A y2 zoom is defined by what it TARGETS, not by its id — gate on the axis
// index so this and the builder cannot drift apart. Used to drop the y2 pair
// with its axis when there is no overlay line (same hasLine gate as the axis
// and its lock handles; no floating handles on a hidden axis).
const isY2DataZoom = (dz: DataZoomEntry): boolean => dz.yAxisIndex === 1;

// Lock-handle placements tuned for THIS widget's grid (top 46 / bottom 100), NOT
// TotalRateDate's (top/bottom 50) — the previous reuse of
// TOTAL_RATE_DATE_PRIMARY_Y_LOCK_POSITION put the handles too low (#2a). MAX sits
// just above the top of the bars axis, MIN just below the axis line, so the
// handles reach the axis extremes (top nudged up, bottom nudged down per user).
// Left handles hug the left axis; the right (y2) handles hug the right axis. [EYEBALL]
const RIG_FRAC_PRIMARY_Y_LOCK_POSITION = {
  min: { bottom: GRID_BOTTOM_ROTATED - 5, left: 18 },
  max: { top: GRID_TOP + 2, left: 18 }
};
const RIG_FRAC_SECONDARY_Y_LOCK_POSITION = {
  min: { bottom: GRID_BOTTOM_ROTATED - 5, right: 24 },
  max: { top: GRID_TOP + 2, right: 24 }
};

// y-axis title gap: the centered vertical name sits this far outside the axis
// line, clear of the integer tick labels but inside the grid's left/right inset.
// 46 puts the title 20px from the widget edge (66 − 46), in line with Status
// Over Time and the wells charts.
const Y_AXIS_NAME_GAP = 46;

const TOOLTIP_MIN_TOP = 48;

// Shared tooltip chrome; every consumer supplies its own trigger, pointer and
// formatter. `enterable` lets the user move into the box and scroll it when a
// group-by yields many rows (max-height lives on the host); safe because it is
// paired with `confine`, not appendToBody (EVA-6690).
export const rigFracTooltip: NonNullable<EChartsOption["tooltip"]> = {
  confine: true,
  enterable: true,
  // Match the wells stacked-bar tooltip's dismiss timing so it lingers the same
  // way (showDelay 500 / hideDelay 0 / show), plus the offset `position` that
  // places the box up-and-away from the cursor so the pointer can move into it.
  showDelay: 500,
  hideDelay: 0,
  show: true,
  className: "chart-tooltip-container",
  extraCssText: "border-style:none;",
  position: function (point, _, dom) {
    const xPos = point[0];
    const height = (dom as HTMLDivElement).offsetHeight;
    const yPos =
      point[1] - height > TOOLTIP_MIN_TOP ? point[1] - height : TOOLTIP_MIN_TOP;
    return [xPos, yPos];
  }
};

// The right inset holds DOM controls (axis selector, lock handles) that paint above
// the confined tooltip, plus the canvas y2 slider an enterable box would block from
// dragging; flip left of the cursor rather than letting `confine` push the box over them.
const rigFracActivityTooltipPosition: TooltipComponentPositionCallback = (
  point,
  _params,
  _dom,
  _rect,
  size
) => {
  const [boxWidth, boxHeight] = size.contentSize;
  const viewWidth = size.viewSize[0];
  const xPos =
    point[0] + boxWidth > viewWidth - GRID_LEFT_RIGHT
      ? Math.max(point[0] - boxWidth, GRID_LEFT_RIGHT)
      : point[0];
  const yPos =
    point[1] - boxHeight > TOOLTIP_MIN_TOP ? point[1] - boxHeight : TOOLTIP_MIN_TOP;
  return [xPos, yPos];
};

/**
 * Whether a series carries a value of the chart's group-by attribute (an
 * operator, a basin) rather than the overlay total, which rides the secondary
 * axis and is not a value of anything. Only the former can take a colour from
 * the attribute's palette, so this is also what decides which labels are handed
 * to the colour resolver.
 */
export function isRigFracValueSeries(series: {
  yAxisIndex?: Maybe<number>;
  isSecondaryYAxis?: Maybe<boolean>;
}): boolean {
  return !(series.yAxisIndex === 1 || series.isSecondaryYAxis === true);
}

/**
 * Looks up a bar's client-resolved colour by its series label, falling back to
 * the caller's own colour (normally the server's `style.hexColor`) on a miss
 * or when the chart isn't client-coloured at all (seriesColors absent).
 */
export function resolveRigFracSeriesColor(
  label: Maybe<string>,
  seriesColors: Maybe<ReadonlyMap<string, string>>,
  fallback: string
): string {
  return seriesColors?.get(normalizeMatchKey(label)) ?? fallback;
}

/**
 * As above, but for series whose labels carry the focus legend's "(123)"
 * well-count affix — the Well Status charts, whose series are keyed by GroupBy's
 * group title.
 *
 * The count has to come off before matching. The legend and the aggregation both
 * decorate their titles with one, and they count DIFFERENT populations (the
 * legend counts every well in the bin; the aggregation counts the ones it could
 * resolve and classify), so the two disagree by a handful and a raw comparison
 * misses exactly when it matters. Deliberately NOT folded into normalizeMatchKey,
 * which is shared with focus colour sync and is specified as an exact match after
 * trim and case-fold — stripping there would change matching for every caller.
 */
export function resolveSeriesColorIgnoringCount(
  label: Maybe<string>,
  seriesColors: Maybe<ReadonlyMap<string, string>>,
  fallback: string
): string {
  return resolveRigFracSeriesColor(
    removeCount(String(label ?? "")),
    seriesColors,
    fallback
  );
}

/**
 * Rig/Frac Activity Over Time (EVA-76). Net-new translator that copies
 * TotalRateDate's dual-y-axis structure but renders stacked BARS on a category
 * axis of time-bucket labels (weekly/monthly/quarterly buckets are computed
 * server-side), with the right-metric total as an overlay line on yAxis 1.
 */
export function translateRigFracActivityResponseToOptions(
  states: Partial<EvaChartStates>
) {
  const { title, layout, series, chartType, seriesColors, request } = states;

  if (!series || series.length === 0) {
    return {};
  }

  // EVA-149. The secondary metric decides whether the overlay line is a
  // price — read off the persisted request (same unsafe cast pattern the
  // selector and locks helper use for this lean, non-wells request shape).
  // Resolved like the controller does before fetching: a chart saved with the
  // legacy leftMetric/rightMetric keys has no primary/secondaryMetric here yet.
  const { primaryMetric, secondaryMetric } = resolveRigFracMetrics(
    request as unknown as Parameters<typeof resolveRigFracMetrics>[0]
  );
  const isPrice = isPriceMetric(secondaryMetric);
  const { groupBy, timeScale } =
    (request as unknown as Maybe<{
      groupBy?: RigFracGroupBy;
      timeScale?: RigFracTimeScale;
    }>) ?? {};

  const bucketLabels = (layout?.xAxis?.data ?? []) as string[];

  // The overlay line is the series the server puts on yAxisIndex 1.
  const barSeries = series.filter(isRigFracValueSeries);
  const lineSeries = series.filter((s) => !isRigFracValueSeries(s));

  // Bars (counts) never carry nulls — coercing to 0 here is unchanged from
  // before this feature.
  const toValues = (y: Maybe<(number | string)[]>): number[] =>
    ((y ?? []) as (number | string)[]).map((v) => Number(v) || 0);

  // EVA-149 (T041). The line path can be a price with genuine null gap weeks
  // (FR-007) — `Number(v) || 0` coerced a null week to a FALSE $0 instead of
  // the gap ECharts renders as a line break for an actual `null` datum.
  // Preserves null; used ONLY for the line series (bars keep toValues/0). A
  // non-numeric-but-non-null value (e.g. a stray string) must ALSO fall back to
  // null rather than NaN — an unguarded NaN would slip past seriesMax's `!=
  // null` filter, reach guardedAxisMax, and land a NaN axis bound in ECharts.
  const toLineValues = (y: Maybe<(number | string | null)[]>): Nullable<number>[] =>
    ((y ?? []) as (number | string | null)[]).map((v) => {
      if (v == null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    });

  // Tooltip only: sum of rounded values so its parts add up to its total. The
  // axis ceiling below uses the raw sums, which is what is actually drawn.
  const bucketTotals = bucketLabels.map((_, bucketIndex) =>
    barSeries.reduce((sum, s) => sum + Math.round(toValues(s.y)[bucketIndex] ?? 0), 0)
  );
  const barsMax = guardedAxisMax(
    // Stacked bars: the axis ceiling is the max SUM across a bucket, not the max single series.
    bucketLabels.length
      ? Math.max(
          ...bucketLabels.map((_, bucketIndex) =>
            barSeries.reduce((sum, s) => sum + (toValues(s.y)[bucketIndex] || 0), 0)
          ),
          0
        )
      : seriesMax(barSeries.map((s) => toValues(s.y))) ?? 0
  );
  // seriesMax filters nulls before reducing, so a null-only (or partially
  // null) line series can't collapse the axis max (H-EC-001).
  //
  // guardedAxisMax clamps any non-positive max to 1 — correct for a count, whose
  // empty/all-zero axis must not be degenerate, and wrong for a price, which can
  // legitimately be negative. The price path takes its own ceiling and falls back
  // to 1 only when the line has no drawable value at all.
  const lineMaxValue = seriesMax(lineSeries.map((s) => toLineValues(s.y)));
  const lineMax = isPrice
    ? lineMaxValue === undefined
      ? 1
      : Math.ceil(lineMaxValue)
    : guardedAxisMax(lineMaxValue ?? 0);
  // EVA-149. Data-derived minimum of the line series — used ONLY to seed the y2
  // MIN lock input's display default for a price metric (see y2MinLockValue
  // below); never forced onto the axis itself.
  const lineMin = seriesMin(lineSeries.map((s) => toLineValues(s.y)));

  // Y-AXIS locks (#6, #2b): user-entered min/max on BOTH the bars (primary) axis
  // and the Frac Crew Count (right/y2) axis. No x/date locks — the custom-range
  // selector handles the window.
  const {
    lockedYMin,
    lockedYMax,
    lockedYMinType,
    lockedYMaxType,
    lockedY2Min,
    lockedY2Max,
    lockedY2MinType,
    lockedY2MaxType
  } = createChartLocks({
    type: EvaChart.RigFracActivity,
    states
  });
  const y1Min = lockedYMin ?? 0;
  // EVA-149. A price right-metric (WTI/Henry Hub, ~$50-70) must NOT be
  // zero-based: pinning the floor to 0 squashes a few-dollar move into a
  // sliver near the top of the plot, which is the exact "flattening" bug this
  // feature exists to fix — just arriving from the min side instead of the
  // max (see y2AxisMax below, which was already left undefined for the
  // equivalent reason). Counts (Rig/Frac Crew Count) keep the zero-based
  // floor — that IS correct for a count axis and must not regress.
  // y2AxisMin is what actually reaches the chart; `scale: true` on yAxis2
  // below tells ECharts not to force zero into the auto-computed range when
  // this is left undefined.
  const y2AxisMin = lockedY2Min ?? (isPrice ? undefined : 0);
  // Axis MAX follows the wells stacked-bar convention: only set when the USER
  // locked it — otherwise leave it undefined so ECharts nice-rounds the ceiling
  // (569 → 600), instead of pinning the axis to the raw data max. minInterval 1
  // keeps the unlocked axis safe (all-zero data → [0, 1], never degenerate).
  // The data-derived maxes still seed the LOCK INPUT display values below.
  const y1AxisMax = lockedYMax;
  const y2AxisMax = lockedY2Max;
  const y1LockValue = lockedYMax ?? barsMax;
  const y2LockValue = lockedY2Max ?? lineMax;
  // Lock-input display value for y2 MIN (what the lock's input box shows when
  // opened — see YMinLocker.tsx, which seeds itself from lock.min.value — not
  // necessarily what's applied to the axis). Counts still default to 0. A
  // price defaults to the data's own minimum instead of 0 — otherwise the
  // input would offer a floor (0) the axis no longer uses. This is an
  // APPROXIMATION of the rendered floor, not an exact readback: y2AxisMin is
  // left undefined for an unlocked price axis (see above) and ECharts'
  // `scale: true` nice-rounding then picks its own floor from the data, the
  // same way the y1LockValue/y2LockValue pair above already approximates the
  // nice-rounded ceiling from barsMax/lineMax rather than matching it exactly
  // ("569 → 600"). Symmetric with that existing, accepted imprecision — not a
  // new one introduced here.
  const y2MinLockValue = lockedY2Min ?? (isPrice ? lineMin ?? 0 : 0);

  // Show every label when there are few buckets (quarterly = 13, monthly), but
  // THIN them once a wide custom/weekly range packs in too many to fit — interval
  // 0 (force-all) made 100+ weekly labels overlap into an unreadable smear. Show
  // at most ~MAX_X_LABELS by keeping every Nth (interval N-1 = skip count).
  const MAX_X_LABELS = 26;
  const xLabelInterval =
    bucketLabels.length <= MAX_X_LABELS
      ? 0
      : Math.ceil(bucketLabels.length / MAX_X_LABELS) - 1;

  const xAxis: Partial<XAXisOption> = {
    name: layout?.xAxis?.title ?? "Time",
    nameLocation: "middle",
    // Title hugs the always-slanted 45° date labels — size-aware so it sits
    // close under short quarterly labels yet clears long custom/weekly ones.
    nameGap: rigFracXNameGap(bucketLabels, DEFAULT_AXIS_LABEL_FONT_SIZE),
    type: "category",
    data: bucketLabels,
    // Explicit splitLine (off — a category axis shows no vertical gridlines by
    // default) so the screenshot X-Axis Major toggle has an object to flip
    // rather than being a no-op (EVA-76 QA).
    splitLine: { show: false },
    axisLabel: {
      fontWeight: DEFAULT_FONT_WEIGHT,
      color: AXIS_LABEL_COLOR,
      fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
      // Slant 45°; thin the labels when there are too many to fit (see above).
      interval: xLabelInterval,
      rotate: 45
    }
  };

  // Persistent y-axis titles from the backend layout (left = bars metric,
  // right = line metric). The hover metric-selector buttons change the metric;
  // these centered vertical titles are what the user always sees.
  const yAxis1: YAXisOptionWithLock = {
    ...createEntityState(ChartOption.YAxis, {
      name: layout?.yAxis?.title ?? "",
      nameLocation: "middle",
      nameGap: Y_AXIS_NAME_GAP,
      nameTextStyle: {
        fontSize: DEFAULT_AXIS_FONT_SIZE,
        color: AXIS_TITLE_COLOR
      },
      // Override DEFAULT_Y_AXIS_OPTIONS.nameTruncate.maxWidth (0), which would
      // otherwise truncate the title to zero width and hide it (the right axis
      // is a plain object that never inherits that default — hence it showed
      // and the left didn't). Mirrors TotalRateDate's nameTruncate override.
      nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
      min: y1Min,
      max: y1AxisMax,
      ...integerValueAxisOptions
    }),
    // Y-axis lock handles (#6). The AxisContainer/AxisLocker read this off the
    // persisted options JSON; mirrors TotalRateDate's primary-y lock. override
    // false — the max is applied directly, no dispatch needed. lockPosition is
    // tuned for OUR grid (#2a), not TotalRateDate's.
    lock: {
      show: true,
      min: { value: y1Min, type: lockedYMinType ?? DataEnum.Number },
      max: {
        value: y1LockValue,
        type: lockedYMaxType ?? DataEnum.Number,
        override: false
      }
    },
    lockPosition: RIG_FRAC_PRIMARY_Y_LOCK_POSITION,
    isTotalRateDateLockPosition: true
  };

  const hasLine = lineSeries.length > 0;
  const yAxis2: YAXisOptionWithLock = {
    id: "yAxis2",
    name: layout?.yAxis2?.[0]?.title ?? "",
    nameGap: Y_AXIS_NAME_GAP,
    nameLocation: "middle",
    nameTextStyle: {
      fontSize: DEFAULT_AXIS_FONT_SIZE,
      color: AXIS_TITLE_COLOR
    },
    position: "right",
    show: hasLine,
    type: "value",
    min: y2AxisMin,
    max: y2AxisMax,
    // EVA-149. Without `scale`, ECharts' default value-axis behaviour forces
    // zero into the auto-computed range whenever `min` is left undefined —
    // exactly what y2AxisMin=undefined (the unlocked price case) is trying to
    // avoid. Harmless when locked/count: an explicit min/max already wins over
    // `scale` regardless.
    scale: isPrice,
    // EVA-149 (T040, H3): a SEPARATE, non-shared object for the price case —
    // yAxis1 above is untouched (still unconditionally integerValueAxisOptions),
    // as are wellStatusCurrent.ts and rigFracRecentActivity.ts, which never see
    // `isPrice` at all.
    ...(isPrice ? priceValueAxisOptions : integerValueAxisOptions),
    // EVA-149 (T042): the crosshair label on THIS axis only — the chart-global
    // `tooltip.axisPointer.label.precision: 0` (rigFracTooltip below) and the
    // left axis are untouched. yAxis.axisPointer overrides the chart-global
    // tooltip.axisPointer for this specific axis.
    ...(isPrice ? { axisPointer: { label: { precision: 2 } } } : {}),
    axisLine: {
      show: true,
      onZero: false,
      lineStyle: { color: "rgb(155,155,155)", width: 1 }
    },
    axisTick: { show: true, lineStyle: { width: 1 } },
    splitLine: { show: false },
    // RIGHT-axis (Frac Crew Count) lock handles (#2b) — only when the overlay
    // line exists. YAxisContainer's y2 branch renders these off lock.show +
    // lockPosition; the YMin/YMaxLocker at yAxisIndex 1 route to locks.y2Min /
    // locks.y2Max for RigFracActivity (see the extended secondary-axis guard).
    lock: {
      show: hasLine,
      min: { value: y2MinLockValue, type: lockedY2MinType ?? DataEnum.Number },
      max: {
        value: y2LockValue,
        type: lockedY2MaxType ?? DataEnum.Number,
        override: false
      }
    },
    lockPosition: RIG_FRAC_SECONDARY_Y_LOCK_POSITION
  };

  const echartsBarSeries = barSeries.map((s, index) => {
    const color = resolveRigFracSeriesColor(
      s.label,
      seriesColors,
      s.style?.hexColor ?? "#000"
    );
    return createEntityState(ChartOption.Bar, {
      id: `${s.label}, ${index}`,
      name: s.label,
      data: toValues(s.y),
      stack: "stack",
      itemStyle: { color },
      z: DEFAULT_Z_INDEX,
      yAxisIndex: 0
    });
  });

  const echartsLineSeries = lineSeries.map((s, index) => {
    // Null-preserving (T041): a genuine gap week in the price series must
    // render as an ECharts line break, not a false $0.
    const data = toLineValues(s.y);
    const color = s.style?.hexColor ?? WELL_COUNT_SERIES_COLOR;
    return createEntityState(ChartOption.LineSeries, {
      id: `${s.label}, line, ${index}`,
      name: s.label,
      data,
      itemStyle: { color },
      lineStyle: {
        width: WELL_COUNT_SERIES_WIDTH,
        color,
        type: "solid"
      },
      // Count what is DRAWABLE, not the array length: a lone value between nulls
      // draws no segment, so its symbol must show. `<= 1` also covers all-null.
      showSymbol: data.filter((v) => v != null).length <= 1,
      // In front of the bars, per spec FR-002.
      z: DEFAULT_Z_INDEX + 1,
      yAxisIndex: 1
    });
  });

  return createEntityState(ChartOption.Chart, {
    chartType,
    toolbox: { show: false },
    // Per-axis dataZoom (#6) — see RIG_FRAC_ACTIVITY_DATA_ZOOM. The y2 pair
    // drops out with its axis when there's no overlay line. Cast like the
    // yAxis/xAxis lines below: the entry interface types what the builder
    // touches, not ECharts' full literal unions.
    dataZoom: (hasLine
      ? RIG_FRAC_ACTIVITY_DATA_ZOOM
      : RIG_FRAC_ACTIVITY_DATA_ZOOM.filter(
          (dz) => !isY2DataZoom(dz)
        )) as unknown as EChartsOption["dataZoom"],
    // Axis trigger (fires anywhere in the bucket column) narrowed by the formatter
    // to the hovered series. No crosshair, as it would imply an all-series tooltip.
    tooltip: {
      ...rigFracTooltip,
      position: rigFracActivityTooltipPosition,
      trigger: "axis",
      axisPointer: { type: "none" },
      formatter: buildRigFracItemTooltipFormatter(
        {
          groupBy: getRigFracGroupByLabel(groupBy),
          metric: rigFracMetricLabel(primaryMetric) ?? layout?.yAxis?.title ?? "",
          bucket: rigFracBucketLabel(timeScale)
        },
        bucketTotals,
        isPrice ? new Set(lineSeries.map((s) => String(s.label ?? ""))) : undefined,
        isPrice ? undefined : rigFracMetricLabel(secondaryMetric),
        states.hoverLegendIdRef
      )
    },
    grid: buildRigFracGrid(true),
    title: {
      ...createEntityState(ChartOption.Title, {
        text: title
      })
    },
    yAxis: [yAxis1, yAxis2] as unknown as YAXisOption[],
    xAxis: xAxis as unknown as XAXisOption,
    series: [...echartsBarSeries, ...echartsLineSeries]
  });
}
